import { LazyLoader, Modal } from '@bp/ui-components';
import { BpPage } from '../../components/BpPage/BpPage';
import { CourseForm } from '../../components/CourseForm/CourseForm';
import { CoursesTableStudent } from '../../components/CoursesTable/CoursesTableStudent';
import { CoursesTableTeacher } from '../../components/CoursesTable/CoursesTableTeacher';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { Suspense, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CourseSettingsForm } from '../../components/CourseSettingsForm/CourseSettingsForm';
import { OrganizationConfigContext } from '../../context/OrganizationConfigContext';
import { graphql } from 'client/gql';
import { useMutation, useQuery } from 'urql';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useConvertToCourseTableType } from './subpages/utils/useConvertToCourseTableType';

const DeleteGroupMutationDocument = graphql(`
  mutation BpDeleteGroupCoursesPage($id: ID!) {
    deleteBpGroup(uuid: $id) {
      entitiesDeleted
    }
  }
`);

const CoursesPagePageDataDocument = graphql(`
  query CoursesPagePageData($profile: ID!, $organizationUuids: [ID!]!) {
    ...PermissionCheckerFields
    ...UseConvertToCourseTableTypeFields
  }
`);

export const CoursesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { pimAuthClaims, updateClaims } = useAuthClaims();
  const profileUuid = pimAuthClaims.getProfile().uuid;
  const organization = pimAuthClaims.getOrganization();
  const [{ data }] = useQuery({
    query: CoursesPagePageDataDocument,
    variables: { profile: profileUuid, organizationUuids: pimAuthClaims.getAllowedOrganizationUuids() },
  });
  const perms = usePermissionChecker(data);
  const canCollaborate = perms?.canUpdateCollaborationGroups(organization);

  const CourseTableData = useConvertToCourseTableType(data);

  const { currentSchoolYear } = useContext(OrganizationConfigContext);

  const [claimsTrigger, setClaimsTrigger] = useState<number>(0);

  const [, deleteCourse] = useMutation(DeleteGroupMutationDocument);

  const [modalMode, setModalMode] = useState<{
    open: boolean;
    mode: 'create' | 'edit' | '';
    courseUuid?: string | null;
  }>({ open: false, mode: '', courseUuid: null });

  const handleAdd = () => {
    setModalMode({ open: true, mode: 'create' });
  };

  const handleEdit = (uuid: string) => {
    setModalMode({ open: true, mode: 'edit', courseUuid: uuid });
  };

  const handleDelete = async (uuid: string) => {
    await deleteCourse({
      id: uuid,
    });
  };

  const [settingsModalOpen, setSettingsModalOpen] = useState<{
    open: boolean;
    courseUuid: string | null;
  }>({ open: false, courseUuid: null });

  const handleSettings = (courseUuid: string) => {
    setSettingsModalOpen({ open: true, courseUuid: courseUuid });
  };

  const handleSettingsClose = () => {
    setSettingsModalOpen({ open: false, courseUuid: null });
  };

  return (
    <BpPage
      title={t('courses.myCourses', { year: currentSchoolYear?.name })}
      isForbidden={false}
      organization={pimAuthClaims.getOrganization()}
    >
      {perms?.canCreateCourse() ? (
        <CoursesTableTeacher
          data={CourseTableData}
          onNavigate={(uuid) => navigate('/courses/' + uuid)}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onDelete={(uuid) => handleDelete(uuid)}
          onSettings={(uuid) => handleSettings(uuid)}
          permissionChecker={perms}
        />
      ) : (
        <CoursesTableStudent data={CourseTableData} onNavigate={(uuid) => navigate('/courses/' + uuid)} />
      )}

      <Modal
        isOpen={modalMode.open}
        onRequestClose={() => setModalMode({ open: false, mode: '', courseUuid: null })}
        title={modalMode.mode === 'edit' ? t('courses.editCourse') : t('courses.addCourse')}
        hideFooter
      >
        <Suspense fallback={<LazyLoader embedded forceHeight='55vh' />}>
          <CourseForm
            canCollaborate={canCollaborate}
            courseUuid={modalMode.courseUuid ?? ''}
            onClose={async () => {
              setModalMode({ open: false, mode: '', courseUuid: null });
              await updateClaims(() => setClaimsTrigger(claimsTrigger + 1));
            }}
            forceUserAsEditor={true}
          />
        </Suspense>
      </Modal>

      <Modal
        title={t('courses.coursesSettings')}
        isOpen={settingsModalOpen.open}
        shouldCloseOnEsc={true}
        onRequestClose={handleSettingsClose}
      >
        <CourseSettingsForm onClose={handleSettingsClose} courseUuid={settingsModalOpen.courseUuid} />
      </Modal>
    </BpPage>
  );
};
